import React, { useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/macro';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, FormControlLabel, MenuItem, Radio, RadioGroup } from '@material-ui/core';
import { Add as AddIcon, Remove as RemoveIcon } from '@material-ui/icons';
import _ from 'lodash';
import IconButton from '@material-ui/core/IconButton';
import { DEFAULT_INPUT_PROPS } from '../../../utilities/FormUtilities';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import NgpRow from '../../form/NgpRow';
import { useDetailStyles } from './WebTestNowStyles';
import { CERTIFICATE_USAGES } from '../../pages-admin/certificates/CertificateConstants';
import { useDataService } from '../../../services/ApiDataService';
import NgpToggle from '../../form/NgpToggle';
import { RequestHeaderValueField } from './RequestHeaderBuilderPopover';
import {
    AUTH_BASIC,
    AUTH_NAMES,
    AUTH_NONE,
    AUTH_NTLM,
    AUTH_USER_CERT,
    EMPTY_POST,
    GET_METHOD,
    HTTP_RESPONSE_CODES,
    METHOD_NAMES,
    POST_METHOD,
} from './WebTestConstants';

export const EMPTY_HEADER = { name: '', value: '' };
export const DEFAULT_HEADER = { name: 'User-Agent', value: 'ngenius_pulse_test_robot' };

export const normalizeRow = (row = {}) => ({
    name: row?.name || '',
    value: row?.value || '',
});

export const normalizeAuthConfig = (auth) => {
    if (!auth) {
        return { type: AUTH_NONE, username: '', password: '' };
    }

    if (typeof auth === 'string') {
        return {
            type: String(auth).toLowerCase(),
            username: '',
            password: '',
        };
    }

    return {
        type: String(auth.type || auth.authType || AUTH_NONE).toLowerCase(),
        username: auth.username || auth.user || '',
        password: auth.password || auth.pass || '',
    };
};

export function WebTestConfig({ test = {} }) {
    const styles = useDetailStyles();
    const dataService = useDataService();
    const { control, setValue } = useFormContext();
    const methodOptions = METHOD_NAMES;
    const testConfig = useMemo(() => test?.testConfig || {}, [test?.testConfig]);
    const {
        fields: postDataFields,
        append: appendPostData,
        insert: insertPostData,
        remove: removePostData,
    } = useFieldArray({
        control,
        name: 'testConfig.postData',
    });
    const {
        fields: httpHeadersFields,
        append: appendHttpHeaders,
        insert: insertHttpHeaders,
        remove: removeHttpHeaders,
    } = useFieldArray({
        control,
        name: 'testConfig.httpHeaders',
    });
    const [currentMethod, setCurrentMethod] = useState(String(testConfig.method || GET_METHOD));
    const showPost = currentMethod === POST_METHOD;
    const [authType, setAuthType] = useState(normalizeAuthConfig(testConfig.auth).type || AUTH_NONE);
    const [allWebCerts, setAllWebCerts] = useState({});

    useEffect(() => {
        const initialize = async () => {
            let currentAuthType = 'none';
            if (testConfig?.auth) {
                // auth exists lets go ahead and mark which so we can fire the correct switch
                if (!_.isEmpty(testConfig.auth?.basic)) {
                    currentAuthType = 'basic';
                } else if (!_.isEmpty(testConfig.auth?.ntlm)) {
                    currentAuthType = 'ntlm';
                } else if (!_.isEmpty(testConfig.auth?.cert)) {
                    currentAuthType = 'cert';
                }
            }
            setAuthType(currentAuthType);

            const webCertQuery = {
                certType: CERTIFICATE_USAGES.web,
                limit: 9999, // we want all web certs as there is no paging in drop down list - right now query will cap max to 3000.
            };
            const url = '/query/admin/nPointCertificate';

            const webCerts = await dataService.get(url, webCertQuery);
            const certs = _.orderBy(webCerts.data, [(w) => w.name.toLowerCase()]);
            setAllWebCerts(certs);

            if (!testConfig?.httpHeaders || testConfig?.httpHeaders?.length === 0) {
                setValue('testConfig', { ...testConfig, httpHeaders: [DEFAULT_HEADER] });
            } else {
                setValue('testConfig', testConfig);
            }
        };

        initialize();
    }, [dataService, setValue, testConfig]);

    useEffect(() => {
        if (postDataFields.length === 0) {
            const configuredPosts = Array.isArray(testConfig.postData) ? testConfig.postData.map(normalizeRow) : [];
            if (configuredPosts.length > 0) {
                appendPostData(configuredPosts);
            } else {
                appendPostData({ ...EMPTY_POST });
            }
        }
    }, [appendPostData, postDataFields.length, testConfig.postData]);

    const handleAddPost = (index) => {
        insertPostData(index + 1, { ...EMPTY_POST });
    };

    const handleRemovePost = (index) => {
        removePostData(index);
    };

    useEffect(() => {
        if (httpHeadersFields.length === 0) {
            const currentHeaders = Array.isArray(testConfig.httpHeaders)
                ? testConfig.httpHeaders.map(normalizeRow)
                : [];
            if (currentHeaders.length > 0) {
                appendHttpHeaders(currentHeaders);
            } else {
                appendHttpHeaders({ ...DEFAULT_HEADER });
            }
        }
    }, [appendHttpHeaders, httpHeadersFields.length, testConfig.httpHeaders]);

    const handleAddHeader = (index) => {
        insertHttpHeaders(index + 1, { ...EMPTY_HEADER });
    };

    const handleRemoveHeader = (index) => {
        removeHttpHeaders(index);
    };

    return (
        <>
            <FormSection title={<Trans>Request Parameters</Trans>}>
                <NgpRow>
                    <NgpSelect
                        fieldName={'testConfig.method'}
                        label={<Trans>Method</Trans>}
                        required
                        onChangeFunc={(event) => {
                            setCurrentMethod(String(event.target.value || GET_METHOD));
                        }}
                    >
                        {_.map(methodOptions, (value, key) => (
                            <MenuItem key={key} value={key}>
                                {value}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                    <NgpTextField
                        fieldName={'testConfig.url'}
                        label={<Trans>URL</Trans>}
                        required
                        inputProps={DEFAULT_INPUT_PROPS}
                    />
                </NgpRow>
                {showPost && (
                    <Box>
                        {postDataFields.map((data, index) => (
                            <Box className={styles.postRow} key={data.id || `post-row-${index}`}>
                                <NgpTextField
                                    fieldName={`testConfig.postData[${index}].name`}
                                    label={<Trans>Post Name</Trans>}
                                    inputProps={DEFAULT_INPUT_PROPS}
                                    fullWidth
                                />
                                <NgpTextField
                                    fieldName={`testConfig.postData[${index}].value`}
                                    label={<Trans>Post Value</Trans>}
                                    inputProps={DEFAULT_INPUT_PROPS}
                                    fullWidth
                                />
                                <IconButton
                                    type="button"
                                    className={styles.actionButton}
                                    onClick={index === 0 ? () => handleAddPost(index) : () => handleRemovePost(index)}
                                >
                                    {index === 0 ? <AddIcon /> : <RemoveIcon />}
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
                <FormSection title={<Trans>Request Headers</Trans>}>
                    <Box>
                        {httpHeadersFields.map((header, index) => (
                            <Box className={styles.headerRow} key={header.id || `header-row-${index}`}>
                                <NgpTextField
                                    fieldName={`testConfig.httpHeaders[${index}].name`}
                                    label={<Trans>Header Name</Trans>}
                                    inputProps={DEFAULT_INPUT_PROPS}
                                    fullWidth
                                />
                                <RequestHeaderValueField
                                    index={index}
                                    fieldNamePrefix="testConfig.httpHeaders"
                                    label={<Trans>Header Value</Trans>}
                                    initialName={header.name}
                                />
                                <IconButton
                                    type="button"
                                    className={styles.actionButton}
                                    onClick={
                                        index === 0 ? () => handleAddHeader(index) : () => handleRemoveHeader(index)
                                    }
                                >
                                    {index === 0 ? <AddIcon /> : <RemoveIcon />}
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </FormSection>
                <FormSection title={<Trans>Authentication</Trans>}>
                    <RadioGroup
                        className={styles.radioWrapper}
                        value={authType}
                        onChange={(event) => {
                            const nextAuthType = event.target.value;
                            setAuthType(nextAuthType);
                            setValue('testConfig.auth.type', nextAuthType, {
                                shouldDirty: true,
                                shouldValidate: false,
                                shouldTouch: true,
                            });
                        }}
                    >
                        {_.map(AUTH_NAMES, (value, key) => (
                            <FormControlLabel key={key} value={key} control={<Radio />} label={value} />
                        ))}
                    </RadioGroup>
                    {authType === AUTH_BASIC && (
                        <>
                            <NgpTextField
                                fieldName={'testConfig.auth.basic.username.value'}
                                label={<Trans>Username</Trans>}
                                required
                                inputProps={DEFAULT_INPUT_PROPS}
                                fullWidth
                            />
                            <NgpTextField
                                fieldName={'testConfig.auth.basic.password.value'}
                                label={<Trans>Password</Trans>}
                                required
                                fieldType="password"
                                inputProps={DEFAULT_INPUT_PROPS}
                                fullWidth
                            />
                        </>
                    )}
                    {authType === AUTH_NTLM && (
                        <>
                            <NgpTextField
                                fieldName={'testConfig.auth.ntlm.username.value'}
                                label={<Trans>Username</Trans>}
                                required
                                inputProps={DEFAULT_INPUT_PROPS}
                                fullWidth
                            />
                            <NgpTextField
                                fieldName={'testConfig.auth.ntlm.password.value'}
                                label={<Trans>Password</Trans>}
                                required
                                fieldType="password"
                                inputProps={DEFAULT_INPUT_PROPS}
                                fullWidth
                            />
                        </>
                    )}
                    {authType === AUTH_USER_CERT && (
                        <NgpSelect
                            className={styles.testSelect}
                            fieldName={'testConfig.auth.cert._id'}
                            label={<Trans>User Certificate</Trans>}
                            required
                        >
                            {_.map(allWebCerts, (value, key) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                    )}
                </FormSection>
                <NgpToggle
                    fieldName={'testConfig.disableServerCertVerification'}
                    label={<Trans>Verify Server Certificate</Trans>}
                    helpTipText={
                        <Trans>
                            Turning this feature OFF will allow the nPoint to ignore server certificate verification
                        </Trans>
                    }
                    inverted
                />
            </FormSection>
            <FormSection title={<Trans>Expected Response</Trans>}>
                <NgpSelect
                    className={styles.testSelect}
                    fieldName={'testConfig.responseCode'}
                    label={<Trans>HTTP Response Code</Trans>}
                    required
                >
                    {_.map(HTTP_RESPONSE_CODES, (value, key) => (
                        <MenuItem key={key} value={key}>
                            {value}
                        </MenuItem>
                    ))}
                </NgpSelect>
                <NgpTextField
                    fieldName={'testConfig.html.httpResponseContains'}
                    label={<Trans>HTML should contain</Trans>}
                    inputProps={{ size: 60, maxLength: 255 }}
                />
                <NgpTextField
                    fieldName={'testConfig.html.httpResponseNotContains'}
                    label={<Trans>HTML should not contain</Trans>}
                    inputProps={{ size: 60, maxLength: 255 }}
                />
            </FormSection>
        </>
    );
}
